import React, { Suspense, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import './App.scss';
import AppLayout from './components/Layouts/AppLayout/AppLayout';
import AuthLayout from './components/Layouts/AuthLayout/AuthLayout';
import Loading from './components/Loading/Loading';
import ApiErrorModal from './components/Modals/ApiErrorModal/ApiErrorModal';
import useClearTokensOnce from './core/hooks/useClearTokensOnce';
import { loadContent } from './core/hooks/useContent';
import useRouterWithLang from './core/hooks/useRouterWithLang';
import { checkToken } from './core/services/app/checkToken/checkToken.slice';
import { setAppMounted } from './core/services/app/setAppMounted/setAppMounted.slice';
import { setPathname } from './core/services/app/setPathname/setPathname.slice';
import { setResponseInterceptor } from './core/services/axios';
import { cancelStaleRequests } from './core/services/requestRegistry';
import { setLoginNavigator } from './core/utilities/navigation';
import { setRemoteConfig } from './core/services/firebase/setRemoteConfig/setRemoteConfig.slice';
import { useAppDispatch, useAppSelector } from './core/services/hooks';
import { AppRouteKey } from './core/utilities/router';
import { Support } from './pages/App/Support/Support';
import Logout from './pages/Auth/Logout/Logout';
import RequireQuery from './components/RequireQuery';
import RegisterActivationSuccess from './components/RegisterActivationSuccess';
import RegisterActivationFail from './components/RegisterActivationFail';
import RegisterActivationCard from './components/RegisterActivationCard';
import ReactivationResult from './components/ReactivationResult';

function App() {
  // checkToken'dan once calismali: eski session ile getMe atilmasini engeller.
  useClearTokensOnce();

  const checkTokenState = useAppSelector((state) => state.app.checkToken);
  const [loadC, setLoadC] = useState<boolean>(false);
  const isAppMounted = useAppSelector(
    (state) => state.app.setAppMounted.isMounted
  );
  const remoteConfigState = useAppSelector(
    (state) => state.firebase.setRemoteConfig
  );
  const me = useAppSelector((state) => state.app.setMe.me);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { provideUrl, navigate } = useRouterWithLang();

  // Interceptor React agacinin disinda; yonlendirme koprusu ONCE kurulmali.
  // Ayni bilesendeki effect'ler tanim sirasiyla kosar, bu yuzden bu effect
  // interceptor'i kuran effect'ten once duruyor.
  useEffect(() => {
    setLoginNavigator(() =>
      navigate(provideUrl(AppRouteKey.LOGIN), { replace: true })
    );
  }, [navigate, provideUrl]);

  useEffect(() => {
    setResponseInterceptor(dispatch);
    dispatch(setRemoteConfig());
    dispatch(checkToken());
    dispatch(setAppMounted(true));
  }, [dispatch]);

  useEffect(() => {
    // Otomatik istek iptali su an KAPALI (requestRegistry -> AUTO_CANCEL_ENABLED),
    // bu cagri no-op. Acik iken: yalnizca damgasi eski pathname olan istekler
    // iptal edilir; yeni sayfanin istekleri (child effect'leri bundan once
    // kostugu icin zaten atilmis olur) yeni pathname ile damgali oldugundan
    // muaf kalir.
    cancelStaleRequests(location.pathname);
    dispatch(setPathname(location.pathname));
    loadContent().then(() => {
      setLoadC(true);
    });
  }, [dispatch, location]);

  const isAppLoading = (): boolean => {
    return (
      !isAppMounted ||
      remoteConfigState.loading ||
      checkTokenState.loading ||
      !loadC
    );
  };

  const isAuthenticated = (): boolean => {
    return !!me;
  };

  const isReactivationRoute = (): boolean => {
    return location.pathname === AppRouteKey.REACTIVATION;
  };

  const renderLoading = () => {
    return <Loading className="app-loading" fontSize={64} />;
  };

  const renderLayout = () => {
    return isAuthenticated() ? (
      <>
        <AppLayout>
          <Routes>
            <Route
              path={provideUrl(AppRouteKey.SUPPORT)}
              element={<Support />}
            />
            <Route
              path={provideUrl(AppRouteKey.HOME)}
              element={<LazyHomePage />}
            />
            <Route
              path={provideUrl(AppRouteKey.PROFILE)}
              element={<LazyProfilePage />}
            />
            <Route
              path={provideUrl(AppRouteKey.CHANGEPASSWORD)}
              element={<LazyChangePasswordPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.LIVECHAT)}
              element={<LazyLiveChatPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.EDITUSERINFORMATION)}
              element={<LazyEditUserInformationPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.EDITCOMPANYINFO)}
              element={<LazyEditCompanyInfoPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.USERS)}
              element={<LazyUsersPage />}
            />
            <Route
              path={provideUrl(`${AppRouteKey.USERS}/:id`)}
              element={<LazyUserDetailsPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.USERCREATE)}
              element={<LazyUserCreatePage />}
            />
            <Route
              path={provideUrl(AppRouteKey.DEMANDS)}
              element={<LazyDemandsPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.CREATEDEMAND)}
              element={<LazyCreateDemandPage />}
            />
            <Route
              path={provideUrl(`${AppRouteKey.DEMANDS}/:id`)}
              element={<LazyDemandsDetailPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.OPERATIONALBILL)}
              element={<LazyOperationalBillPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.PROBLEMATICBILL)}
              element={<LazyProblematicBillPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.CREDITCARDPAY)}
              element={<LazyInvoicePaymentOperationsPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.OTHERREFLECTION)}
              element={<LazyOtherReflectionPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.FINANCIALBILL)}
              element={<LazyFinancialBillPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.ADDITIONALSERVICES)}
              element={<LazyAdditionalServicesPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.KDVRATESMANAGEMENT)}
              element={<LazyKdvRatesManagementPage />}
            />
            <Route
              path={provideUrl(`${AppRouteKey.KDVRATESMANAGEMENT}/:id`)}
              element={<LazyKdvRatesDetailPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.INTERESTMANAGEMENT)}
              element={<LazyInterestManagementPage />}
            />
            <Route
              path={provideUrl(`${AppRouteKey.INTERESTMANAGEMENT}/:id`)}
              element={<LazyInterestManagementDetailPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.INTERESTINFO)}
              element={<LazyInterestInfoPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.CURRENTACCOUNTEXTRACT)}
              element={<LazyCurrentAccountExtractPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.BALANCECONFIRMATION)}
              element={<LazyBalanceReconciliationConfirmationPage />}
            />
            <Route
              path={provideUrl(`${AppRouteKey.CAMPAIGNNEWS}/:type/:id`)}
              element={<LazyCampaignDetailPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.NOTIFICATIONCREATE)}
              element={<LazyNotificationCreate />}
            />
            <Route
              path={provideUrl(`${AppRouteKey.NOTIFICATIONDETAIL}/:id`)}
              element={<LazyNotificationDetail />}
            />
            <Route
              path={provideUrl(`${AppRouteKey.USERLISTDETAIL}/:id`)}
              element={<LazyUserListDetail />}
            />
            <Route
              path={provideUrl(AppRouteKey.USERLISTCREATE)}
              element={<LazyUserListCreate />}
            />
            <Route
              path={provideUrl(AppRouteKey.ESTIMATEDOVERKMCALCULATION)}
              element={<LazyEstimatedOverKmCalculationPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.ESTIMATEDOVERKMCALCULATIONRESULT)}
              element={<LazyEstimatedOverKmCalculationResultPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.EARLYRETURNPENALTYFEECALCULATION)}
              element={<LazyEarlyReturnPenaltyFeeCalculationPage />}
            />
            <Route
              path={provideUrl(
                AppRouteKey.EARLYRETURNPENALTYFEECALCULATIONRESULT
              )}
              element={<LazyEarlyReturnPenaltyFeeCalculationResultPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.DRIVERTRACKING)}
              element={<LazyDriverTrackingPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.DRIVERCHANGES)}
              element={<LazyDriverChangesPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.HGSINFO)}
              element={<LazyHgsInfoPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.CARDELIVERY)}
              element={<LazyCarDeliveryPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.NEWOFFER)}
              element={<LazyNewOfferPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.FLEETINFORMATION)}
              element={<LazyFleetCarInformaitonPage />}
            />
            <Route
              path={provideUrl(`${AppRouteKey.FLEETINFORMATION}/:id`)}
              element={<LazyFleetCarDetailPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.CARRETURN)}
              element={<LazyCarReturnPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.CARRETURNREQUEST)}
              element={<LazyCarReturnRequestPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.DOCUMENTS)}
              element={<LazyDocumentsPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.CAMPAIGNNEWS)}
              element={<LazyCampaignNewsPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.MAINTENANCE)}
              element={<LazyMaintenancePage />}
            />
            <Route
              path={provideUrl(AppRouteKey.MAINTENANCEAPPOINTMENTCREATE)}
              element={<LazyMaintenanceAppointmentCreatePage />}
            />
            <Route
              path={provideUrl(`${AppRouteKey.MAINTENANCE}/:id`)}
              element={<LazyMaintenanceAppointmentDetailPage />}
            />
            <Route
              path={provideUrl(`${AppRouteKey.MAINTENANCEHISTORY}/:id`)}
              element={<LazyMaintenanceHistoryDetailPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.BREAKDOWN)}
              element={<LazyBreakdownPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.BREAKDOWNAPPOINTMENTCREATE)}
              element={<LazyBreakdownAppointmentCreatePage />}
            />
            <Route
              path={provideUrl(`${AppRouteKey.BREAKDOWN}/:id`)}
              element={<LazyBreakdownAppointmentDetailPage />}
            />
            <Route
              path={provideUrl(`${AppRouteKey.BREAKDOWNHISTORY}/:id`)}
              element={<LazyBreakdownHistoryDetailPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.TYRE)}
              element={<LazyTyrePage />}
            />
            <Route
              path={provideUrl(AppRouteKey.TYREDETAİL)}
              element={<LazyTyreAppointmentDetailPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.TYREAPPOINTMENTCREATE)}
              element={<LazyTyreAppointmentCreatePage />}
            />
            <Route
              path={provideUrl(`${AppRouteKey.OBJECTION}/:id`)}
              element={<LazyObjectionRequestPage />}
            />
            <Route
              path={provideUrl(`${AppRouteKey.PROCESSUALDEMANDS}/:id`)}
              element={<LazyProcessualDemandsDetail />}
            />
            <Route
              path={provideUrl(`${AppRouteKey.TYREHISTORY}/:id`)}
              element={<LazyTyreHistoryDetailPage />}
            />
            <Route
              path={provideUrl(`${AppRouteKey.PROCESSUALDEMANDS}`)}
              element={<LazyProcessualDemands />}
            />
            <Route
              path={provideUrl(AppRouteKey.ACCIDENTALDAMAGE)}
              element={<LazyAccidentalDamagePage />}
            />
            <Route
              path={provideUrl(AppRouteKey.PROCESSUALDEMANDSCREATE)}
              element={<LazyProcessualDemandsCreate />}
            />
            --
            <Route
              path={provideUrl(`${AppRouteKey.OPERATIONALBILLREQ}`)}
              element={<LazyOperationalBillCreatePage />}
            />
            <Route
              path={provideUrl(`${AppRouteKey.FINANCIALBILLREQ}`)}
              element={<LazyFinancialBillCreatePage />}
            />
            <Route
              path={provideUrl(AppRouteKey.ADDITIONALBILLREQ)}
              element={<LazyAdditionalBillCreatePage />}
            />
            <Route
              path={provideUrl(AppRouteKey.OTHERBILLREQ)}
              element={<LazyOtherBillCreatePage />}
            />
            <Route
              path={provideUrl(AppRouteKey.ACCIDENTALDAMAGECREATE)}
              element={<LazyAccidentalDamageCreatePage />}
            />
            <Route
              path={provideUrl(`${AppRouteKey.ACCIDENTALDAMAGE}/:id`)}
              element={<LazyAccidentalDamageAppointmentDetailPage />}
            />
            <Route
              path={provideUrl(`${AppRouteKey.ACCIDENTALDAMAGEHISTORY}/:id`)}
              element={<LazyAccidentalDamageHistoryDetailPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.FAQ)}
              element={<LazyFaqPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.GUIDE)}
              element={<LazyGuidePage />}
            />
            <Route
              path={provideUrl(AppRouteKey.HELP_DIGITAL_ASSISTANT)}
              element={<LazyHelpDigitalAssistantPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.DRIVERVIDEOS)}
              element={<LazyDriverVideosPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.FEEDBACK)}
              element={<LazyFeedbackPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.OVERDUE_INVOICE)}
              element={<LazyOverdueInvoicePage />}
            />
            <Route
              path={provideUrl(AppRouteKey.NEWPASSWORD)}
              element={<Logout />}
            />
            <Route
              path={provideUrl(AppRouteKey.COPILOTNEWPASSWORD)}
              element={<Logout />}
            />
            <Route
              path={provideUrl(AppRouteKey.ACTIVATION_SUCCESS)}
              element={
                <RequireQuery
                  required={['userId', 'userNameSurname']}
                  redirectTo={provideUrl(AppRouteKey.HOME)}
                >
                  <div className={'w-full flex justify-center'}>
                    <RegisterActivationSuccess />
                  </div>
                </RequireQuery>
              }
            />
            <Route
              path={provideUrl(AppRouteKey.ACTIVATION_FAIL)}
              element={
                <div className={'w-full flex justify-center'}>
                  <RegisterActivationFail />
                </div>
              }
            />
            <Route
              path={provideUrl(AppRouteKey.ACTIVATION)}
              element={
                <RequireQuery
                  required={[
                    'uyeId',
                    'hash',
                    'uyeAd',
                    'uyeSoyad',
                    'uyeEmail',
                    'uyeTelefon',
                    'uyePlate',
                  ]}
                  redirectTo={provideUrl(AppRouteKey.HOME)}
                >
                  <div className={'w-full flex justify-center'}>
                    <RegisterActivationCard />
                  </div>
                </RequireQuery>
              }
            />
            <Route
              path="*"
              element={<Navigate to={provideUrl(AppRouteKey.HOME)} />}
            />
          </Routes>
        </AppLayout>
      </>
    ) : (
      <>
        <AuthLayout>
          <Routes>
            <Route
              path={provideUrl(AppRouteKey.LOGIN)}
              element={<LazyLoginPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.FORGETPASSWORD)}
              element={<LazyForgetPasswordPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.CHOOSECOMPANY)}
              element={<LazyChooseCompanyPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.NEWPASSWORD)}
              element={<LazyNewPasswordPage />}
            />
            <Route
              path={provideUrl(AppRouteKey.COPILOTNEWPASSWORD)}
              element={<LazyNewPasswordPage variant="copilot" />}
            />
            <Route
              path={provideUrl(AppRouteKey.ACTIVATION)}
              element={
                <RequireQuery
                  required={[
                    'uyeId',
                    'hash',
                    'uyeAd',
                    'uyeSoyad',
                    'uyeEmail',
                    'uyeTelefon',
                    'uyePlate',
                  ]}
                  redirectTo={provideUrl(AppRouteKey.HOME)}
                >
                  <div className={'w-full flex justify-center'}>
                    <RegisterActivationCard />
                  </div>
                </RequireQuery>
              }
            />
            <Route
              path={provideUrl(AppRouteKey.ACTIVATION_SUCCESS)}
              element={
                <RequireQuery
                  required={['userId', 'userNameSurname']}
                  redirectTo={provideUrl(AppRouteKey.HOME)}
                >
                  <div className={'w-full flex justify-center'}>
                    <RegisterActivationSuccess />
                  </div>
                </RequireQuery>
              }
            />
            <Route
              path={provideUrl(AppRouteKey.ACTIVATION_FAIL)}
              element={
                <div className={'w-full flex justify-center'}>
                  <RegisterActivationFail />
                </div>
              }
            />
            <Route
              path="*"
              element={<Navigate to={provideUrl(AppRouteKey.LOGIN)} />}
            />
          </Routes>
        </AuthLayout>
      </>
    );
  };

  return (
    <div id="app">
      {isReactivationRoute() ? (
        loadC ? (
          <ReactivationResult />
        ) : (
          renderLoading()
        )
      ) : (
        <Suspense fallback={renderLoading()}>
          {isAppLoading() ? renderLoading() : renderLayout()}
          <ApiErrorModal />
        </Suspense>
      )}
    </div>
  );
}

export default App;

const LazyHomePage = React.lazy(() => import('./pages/App/HomePage/HomePage'));
const LazyLoginPage = React.lazy(
  () => import('./pages/Auth/LoginPage/LoginPage')
);
const LazyForgetPasswordPage = React.lazy(
  () => import('./pages/Auth/ForgetPasswordPage/ForgetPasswordPage')
);
const LazyChooseCompanyPage = React.lazy(
  () => import('./pages/Auth/ChooseCompanyPage/ChooseCompanyPage')
);
const LazyNewPasswordPage = React.lazy(
  () => import('./pages/Auth/NewPasswordPage/NewPasswordPage')
);
const LazyProfilePage = React.lazy(
  () => import('./pages/App/ProfilePage/ProfilePage')
);
const LazyEditUserInformationPage = React.lazy(
  () =>
    import(
      './pages/App/ProfilePage/EditUserInformationPage/EditUserInformationPage'
    )
);
const LazyEditCompanyInfoPage = React.lazy(
  () =>
    import('./pages/App/ProfilePage/EditCompanyInfoPage/EditCompanyInfoPage')
);
const LazyUsersPage = React.lazy(
  () => import('./pages/App/Users/UsersPage/UsersPage')
);
const LazyUserDetailsPage = React.lazy(
  () => import('./pages/App/Users/UserDetailPage/UserDetailPage')
);
const LazyUserCreatePage = React.lazy(
  () => import('./pages/App/Users/UserCreatePage/UserCreatePage')
);
const LazyDemandsPage = React.lazy(
  () => import('./pages/App/UserDemands/DemandsPage/DemandsPage')
);
const LazyCreateDemandPage = React.lazy(
  () => import('./pages/App/UserDemands/CreateDemandPage/CreateDemandPage')
);
const LazyDemandsDetailPage = React.lazy(
  () => import('./pages/App/UserDemands/DemandsDetailPage/DemandsDetailPage')
);

const LazyOperationalBillPage = React.lazy(
  () => import('./pages/App/Bills/OperationalBillPage/OperationalBillPage')
);
const LazyProblematicBillPage = React.lazy(
  () => import('./pages/App/Bills/ProblematicBillPage/ProblematicBillPage')
);
const LazyOtherReflectionPage = React.lazy(
  () => import('./pages/App/Bills/OtherReflectionPage/OtherReflectionPage')
);
const LazyFinancialBillPage = React.lazy(
  () => import('./pages/App/Bills/FinancialBillPage/FinancialBillPage')
);
const LazyAdditionalServicesPage = React.lazy(
  () =>
    import('./pages/App/Bills/AdditionalServicesPage/AdditionalServicesPage')
);
const LazyKdvRatesManagementPage = React.lazy(
  () =>
    import(
      './pages/App/Financial/KdvRatesManagementPage/KdvRatesManagementPage'
    )
);
const LazyKdvRatesDetailPage = React.lazy(
  () =>
    import(
      './pages/App/Financial/KdvRatesManagementPage/KdvRatesDetailPage/KdvRatesDetailPage'
    )
);
const LazyInterestManagementPage = React.lazy(
  () =>
    import(
      './pages/App/Financial/InterestManagementPage/InterestManagementPage'
    )
);
const LazyInterestManagementDetailPage = React.lazy(
  () =>
    import(
      './pages/App/Financial/InterestManagementPage/InterestManagementDetailPage/InterestManagementDetailPage'
    )
);

const LazyInterestInfoPage = React.lazy(
  () => import('./pages/App/Financial/InterestInfoPage/InterestInfoPage')
);

const LazyCurrentAccountExtractPage = React.lazy(
  () =>
    import(
      './pages/App/Financial/CurrentAccountExtractPage/CurrentAccountExtractPage'
    )
);

const LazyBalanceReconciliationConfirmationPage = React.lazy(
  () =>
    import(
      './pages/App/Financial/BalanceReconciliationConfirmationPage/BalanceReconciliationConfirmationPage'
    )
);

const LazyEstimatedOverKmCalculationPage = React.lazy(
  () =>
    import(
      './pages/App/Financial/EstimatedOverKmCalculation/EstimatedOverKmCalculationPage/EstimatedOverKmCalculationPage'
    )
);

const LazyEstimatedOverKmCalculationResultPage = React.lazy(
  () =>
    import(
      './pages/App/Financial/EstimatedOverKmCalculation/EstimatedOverKmCalculationResultPage/EstimatedOverKmCalculationResultPage'
    )
);

const LazyEarlyReturnPenaltyFeeCalculationPage = React.lazy(
  () =>
    import(
      './pages/App/Financial/EarlyReturnPenaltyFeeCalculation/EarlyReturnPenaltyFeeCalculationPage/EarlyReturnPenaltyFeeCalculationPage'
    )
);

const LazyEarlyReturnPenaltyFeeCalculationResultPage = React.lazy(
  () =>
    import(
      './pages/App/Financial/EarlyReturnPenaltyFeeCalculation/EarlyReturnPenaltyFeeCalculationResultPage/EarlyReturnPenaltyFeeCalculationResultPage'
    )
);

const LazyDriverTrackingPage = React.lazy(
  () =>
    import(
      './pages/App/Financial/EarlyReturnPenaltyFeeCalculation/DriverTrackingPage/DriverTrackingPage'
    )
);

const LazyDriverChangesPage = React.lazy(
  () =>
    import('./pages/App/FleetCarManagement/DriverChangesPage/DriverChangesPage')
);
const LazyHgsInfoPage = React.lazy(
  () => import('./pages/App/FleetCarManagement/HgsInfoPage/HgsInfoPage')
);
const LazyFleetCarInformaitonPage = React.lazy(
  () =>
    import(
      './pages/App/FleetCarManagement/FleetCarInformation/FleetCarInformationPage'
    )
);
const LazyFleetCarDetailPage = React.lazy(
  () =>
    import(
      './pages/App/FleetCarManagement/FleetCarInformation/FleetCarDetailPage/FleetCarDetailPage'
    )
);
const LazyCarReturnPage = React.lazy(
  () => import('./pages/App/FleetCarManagement/CarReturn/CarReturnPage')
);
const LazyDocumentsPage = React.lazy(
  () => import('./pages/App/FleetCarManagement/DocumentsPage/DocumentsPage')
);
const LazyCampaignNewsPage = React.lazy(
  () => import('./pages/App/CampaignNews/CampaignNewsPage/CampaignNewsPage')
);
const LazyMaintenancePage = React.lazy(
  () => import('./pages/App/ServiceOperation/MaintenancePage/MaintenancePage')
);
const LazyMaintenanceAppointmentCreatePage = React.lazy(
  () =>
    import(
      './pages/App/ServiceOperation/MaintenancePage/MaintenanceAppointmentCreatePage/MaintenanceAppointmentCreatePage'
    )
);
const LazyMaintenanceAppointmentDetailPage = React.lazy(
  () =>
    import(
      './pages/App/ServiceOperation/MaintenancePage/MaintenanceAppointmentDetailPage/MaintenanceAppointmentDetailPage'
    )
);
const LazyMaintenanceHistoryDetailPage = React.lazy(
  () =>
    import(
      './pages/App/ServiceOperation/MaintenancePage/MaintenanceHistoryDetailPage/MaintenanceHistoryDetailPage'
    )
);
const LazyBreakdownPage = React.lazy(
  () => import('./pages/App/ServiceOperation/BreakdownPage/BreakdownPage')
);

const LazyBreakdownAppointmentCreatePage = React.lazy(
  () =>
    import(
      './pages/App/ServiceOperation/BreakdownPage/BreakdownAppointmenCreatePage/BreakdownAppointmentCreatePage'
    )
);

const LazyBreakdownAppointmentDetailPage = React.lazy(
  () =>
    import(
      './pages/App/ServiceOperation/BreakdownPage/BreakdownAppointmentDetailPage/BreakdownAppointmentDetailPage'
    )
);
const LazyBreakdownHistoryDetailPage = React.lazy(
  () =>
    import(
      './pages/App/ServiceOperation/BreakdownPage/BreakdownHistoryDetailPage/BreakdownHistoryDetailPage'
    )
);
const LazyTyrePage = React.lazy(
  () => import('./pages/App/ServiceOperation/TyrePage/TyrePage')
);

const LazyTyreAppointmentDetailPage = React.lazy(
  () =>
    import(
      './pages/App/ServiceOperation/TyrePage/TyreAppointmentDetailPage/TyreAppointmentDetailPage'
    )
);
const LazyTyreHistoryDetailPage = React.lazy(
  () =>
    import(
      './pages/App/ServiceOperation/TyrePage/TyreHistoryDetailPage/TyreHistoryDetailPage'
    )
);
const LazyTyreAppointmentCreatePage = React.lazy(
  () =>
    import(
      './pages/App/ServiceOperation/TyrePage/TyreAppointmentCreatePage/TyreAppointmentCreatePage'
    )
);
const LazyAccidentalDamagePage = React.lazy(
  () =>
    import(
      './pages/App/ServiceOperation/AccidentalDamagePage/AccidentalDamagePage'
    )
);
const LazyAccidentalDamageCreatePage = React.lazy(
  () =>
    import(
      './pages/App/ServiceOperation/AccidentalDamagePage/AccidentalDamageAppointmenCreatePage/AccidentalDamageAppointmentCreatePage'
    )
);
const LazyAccidentalDamageAppointmentDetailPage = React.lazy(
  () =>
    import(
      './pages/App/ServiceOperation/AccidentalDamagePage/AccidentalDamageAppointmentDetailPage/AccidentalDamageAppointmentDetailPage'
    )
);
const LazyAccidentalDamageHistoryDetailPage = React.lazy(
  () =>
    import(
      './pages/App/ServiceOperation/AccidentalDamagePage/AccidentalDamageHistoryDetailPage/AccidentalDamageHistoryDetailPage'
    )
);
const LazyFaqPage = React.lazy(
  () => import('./pages/App/Help/FaqPage/FaqPage')
);
const LazyCarDeliveryPage = React.lazy(
  () => import('./pages/App/FleetCarManagement/CarDeliveryPage/CarDeliveryPage')
);
const LazyNewOfferPage = React.lazy(
  () =>
    import(
      './pages/App/FleetCarManagement/CarDeliveryPage/NewOfferPage/NewOfferPage'
    )
);
const LazyCarReturnRequestPage = React.lazy(
  () =>
    import(
      './pages/App/FleetCarManagement/CarReturn/CarReturnRequestPage/CarReturnRequestPage'
    )
);

const LazyObjectionRequestPage = React.lazy(
  () => import('./pages/App/Bills/ObjectionRequest/ObjectionRequestPage')
);

const LazyChangePasswordPage = React.lazy(
  () => import('./pages/App/ProfilePage/ChangePassword/ChangePasswordPage')
);

const LazyGuidePage = React.lazy(() => import('./pages/App/Help/Guide/Guide'));

const LazyHelpDigitalAssistantPage = React.lazy(
  () => import('./pages/App/Help/DigitalAssistant/DigitalAssistant')
);

const LazyFeedbackPage = React.lazy(
  () => import('./pages/App/Help/Feedback/Feedback')
);

const LazyProcessualDemands = React.lazy(
  () => import('./pages/App/UserDemands/ProcessualDemands/ProcessualDemands')
);

const LazyProcessualDemandsDetail = React.lazy(
  () =>
    import('./pages/App/UserDemands/ProcessualDemands/ProcessualDemandsDetail')
);

const LazyProcessualDemandsCreate = React.lazy(
  () =>
    import('./pages/App/UserDemands/ProcessualDemands/ProcessualDemandsCreate')
);

const LazyLiveChatPage = React.lazy(
  () => import('./pages/App/LiveChat/LiveChat')
);

const LazyCampaignDetailPage = React.lazy(
  () => import('./pages/App/CampaignNews/Detail/CampaignDetail')
);

const LazyOtherBillCreatePage = React.lazy(
  () => import('./pages/App/Bills/ObjectionReqs/OtherReq')
);
const LazyAdditionalBillCreatePage = React.lazy(
  () => import('./pages/App/Bills/ObjectionReqs/AdditionalReq')
);
const LazyFinancialBillCreatePage = React.lazy(
  () => import('./pages/App/Bills/ObjectionReqs/FinancialReq')
);
const LazyOperationalBillCreatePage = React.lazy(
  () => import('./pages/App/Bills/ObjectionReqs/OperationalReq')
);

const LazyInvoicePaymentOperationsPage = React.lazy(
  () =>
    import(
      './pages/App/Financial/InvoicePaymentOperationsPage/InvoicePaymentOperationsPage'
    )
);
const LazyDriverVideosPage = React.lazy(
  () => import('./pages/App/Help/DriverVideos/DriverVideos')
);

const LazyOverdueInvoicePage = React.lazy(
  () => import('./pages/App/OverdueInvoicePage')
);

const LazyNotificationCreate = React.lazy(
  () =>
    import(
      './pages/App/CampaignNews/AddNewContentPage/Create/Notification/AddNewNotification'
    )
);

const LazyNotificationDetail = React.lazy(
  () =>
    import(
      './pages/App/CampaignNews/AddNewContentPage/Detail/Notification/NotificationDetail'
    )
);

const LazyUserListDetail = React.lazy(
  () =>
    import(
      './pages/App/CampaignNews/AddNewContentPage/Detail/UserList/UserListDetail'
    )
);

const LazyUserListCreate = React.lazy(
  () =>
    import(
      './pages/App/CampaignNews/AddNewContentPage/Create/UserList/UserListPage'
    )
);
